import { useState } from 'react';
import { ICacheItem } from './types';

const useLocalStorageCache = <T>(key: string, initialData: T, maxAge: number): [T | null, (value: T) => void] => {
    const now = new Date().getTime();

    // Sets value or null
    const value = (): T | null => {
        const item = localStorage.getItem(key);
        // If the item doesn't exist, use initial data instead
        if (!item) {
            return initialData;
        }

        // Check if cache item has expired, if it has, remove it and return null so a new data set can be retrieved,
        // otherwise returns data stored in cache
        const parsedItem: ICacheItem<T> = JSON.parse(item);
        if (now - parsedItem.time > maxAge) {
            localStorage.removeItem(key);
            return null;
        }

        return parsedItem.value;
    };

    const [storedValue, setStoredValue] = useState<T | null>(value);

    // Sets item in local storage, at this point, the data for a given key shouldn't exist and therefore
    // should be set here to be checked again on next reload.
    const setValue = (value: T) => {
        localStorage.setItem(key, JSON.stringify({ value, time: new Date().getTime() }));
        setStoredValue(value);
    }

    return [storedValue, setValue];
}

export default useLocalStorageCache